/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================
 
 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)
 
 Coded by www.creative-tim.com
 
 =========================================================
 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// @mui material components

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 PRO React examples
import DataTable from "examples/Tables/DataTable";

//React Hooks and Others
import React, { useState } from "react";
import { CircularProgress, Icon, Tooltip } from "@mui/material";
import MDSelect from "components/MDSelect";
import PropTypes from "prop-types";

import ComponentsPrivatization from "config/component_privatization";
import { style_icon } from "helpers/global_vars";
import { ESTUDIANTES_LIST_URL, ESTUDIANTES_NEW_URL } from "config/bases_url";
import { useRedirectForm } from "hooks/useRedirectForm";
import {
  useDeleteEstudianteMutation,
  useGetCarrerasQuery,
  useGetEstudiantesQuery,
  useGetGrupoEscolarQuery,
} from "services";
import { AUXILIAR_DOCENTE, COORDINADOR_DOCENTE } from "roles";
import usePagination from "hooks/usePagination";
import ExcelModal from "../../../../components/ModalExcel/excel.modal";
import EstudianteShow from "./estudianteShow";
import useModalHandler from "../../../../hooks/useModalHandler";
import { handleToast } from "../../../../helpers/handleToast";
import excelFile from "assets/templates/Importar_estudiantes.xlsx";
import NewStudent from "../../ModalCreate/NewStudent/newStudent";
import NewAntecedente from "../../ModalCreate/NewAntecedente/newAntecedente";
import AntecedentesShow from "./AntecedentesShow";

export const FiltersStudentComponent = ({
  values,
  handleChange,
  grupos,
  carreras,
}) => {
  const renderSelectedValue = (selectedValue) => {
    let color = "";
    let text = "";
    let iconColor = "";

    switch (selectedValue) {
      case "Activo":
        color = "#2e7d32";
        text = "Activos";
        iconColor = "#2e7d32";
        break;
      case "Baja Temporal":
        color = "#f5e617"; // Color del texto
        text = "Baja Temporal";
        iconColor = "#f5e617"; // Color del icono, puede ser el mismo o uno que contraste mejor
        break;
      case "Baja Definitiva":
        color = "#f33535";
        text = "Baja Definitiva";
        iconColor = "#f33535";
        break;
      case "Graduado":
        color = "#ff5e2d";
        text = "Graduados";
        iconColor = "#ff5e2d";
        break;
      default:
        return selectedValue;
    }

    return (
      <MDBox sx={{ display: "flex", alignItems: "center" }}>
        <Icon
          sx={{ color: iconColor, marginRight: "8px", fontSize: "1.25rem" }}
        >
          lightbulb
        </Icon>
        <MDTypography variant="inherit" sx={{ color: color }}>
          {text}
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <MDBox
      display="flex"
      flexDirection={{ xs: "column", lg: "row" }}
      alignItems="center"
      gap={2}
      width="100%"
      mb={5}
    >
      <MDBox sx={{ minWidth: "10rem", width: "100%" }}>
        <MDSelect
          label="Estado"
          options={[
            { value: "Activo", label: "Activos" },
            { value: "Baja Temporal", label: "Baja Temporal" },
            { value: "Baja Definitiva", label: "Baja Definitiva" },
            { value: "Graduado", label: "Graduados" },
          ]}
          value={values.estado}
          onChange={(val) =>
            handleChange({ target: { name: "estado", value: val } })
          }
          renderDisplay={(selectedValue) => renderSelectedValue(selectedValue)}
        />
      </MDBox>

      <MDBox sx={{ minWidth: "10rem", width: "100%" }}>
        <MDSelect
          label="Carrera"
          options={[
            { value: "", label: "Limpiar" },
            ...(carreras
              ?.filter(
                (obj, index, self) =>
                  index === self.findIndex((t) => t.id === obj.id),
              )
              .map((el) => ({
                value: el.id,
                label: `${el.nombre} ${el.plan_de_estudio}`,
              })) || []),
          ]}
          value={values.carrera}
          onChange={(val) =>
            handleChange({ target: { name: "carrera", value: val } })
          }
        />
      </MDBox>

      <MDBox sx={{ minWidth: "10rem", width: "100%" }}>
        <MDSelect
          label="Grupo"
          options={[
            { value: "", label: "Limpiar" },
            { value: "Sin Asignar", label: "Sin Asignar" },
            ...(grupos
              ?.filter(
                (obj, index, self) =>
                  index === self.findIndex((t) => t.id === obj.id),
              )
              .map((el) => ({ value: el.id, label: el.identificador })) || []),
          ]}
          value={values.grupo}
          onChange={(val) =>
            handleChange({ target: { name: "grupo", value: val } })
          }
        />
      </MDBox>
    </MDBox>
  );
};

function DataTableEstudiantes({ tableHeader }) {
  const { open, handleOpen, handleClose } = useModalHandler();
  const {
    open: openStudentModal,
    handleOpen: handleOpenStudentModal,
    handleClose: handleCloseStudentModal,
  } = useModalHandler();

  const {
    open: openAntecedenteModal,
    handleOpen: handleOpenAntecedenteModal,
    handleClose: handleCloseAntecedenteModal,
  } = useModalHandler();

  const [studentIdToEdit, setStudentIdToEdit] = useState(null);
  const [selectedStudentForAntecedente, setSelectedStudentForAntecedente] =
    useState(null);

  const [values, setValues] = useState({
    carrera: "",
    grupo: "",
    estado: "Activo",
  });

  const handleChange = (e) => {
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  const {
    offset,
    setOffset,
    limit,
    busqueda,
    setBusqueda,
    handleNext,
    handlePrevious,
  } = usePagination();

  const { data: carrerasOrdenadas } = useGetCarrerasQuery(
    {
      filter_params: {
        estudiantes: true,
        ordering: "nombre,plan_de_estudio",
        query: "{plan_de_estudio, nombre,id,}",
      },
      shape: {
        id: "id",
        nombre: "nombre",
        plan_de_estudio: "plan_de_estudio",
      },
    },
    { refetchOnReconnect: true },
  );

  const { data: gruposOrdenados } = useGetGrupoEscolarQuery({
    filter_params: {
      estudiantes: true,
      carrera: values.carrera,
      ordering: "identificador",
      query: "{id,identificador}",
    },
    shape: {
      id: "id",
      identificador: "identificador",
    },
  });

  const {
    data: estudiantes_data,
    isError: isErrorG,
    isLoading: isLoadingG,
    error: errorG,
    isFetching,
  } = useGetEstudiantesQuery(
    {
      filter_params: {
        ...(values.estado !== "" ? { estado: values.estado } : {}),
        ...(values.grupo !== ""
          ? values.grupo === "Sin Asignar"
            ? { asignado: "false" }
            : { grupo: values.grupo }
          : {}),
        buscar: busqueda,
        ordering:
          "user__apellido_paterno,user__apellido_materno,user__first_name,numero_de_control",
        limit: limit,
        offset: offset,
        preasignacion_de_carrera: values.carrera,
        query:
          "{user{first_name,apellido_materno,apellido_paterno,foto,email},numero_de_control,url,id,curp,genero{genero},tipo_sangre,telefonos}",
      },
      shape: {
        id: "id",
        url: "url",
        nombre: "user.first_name",
        numero_de_control: "numero_de_control",
        apellido_materno: "user.apellido_materno",
        apellido_paterno: "user.apellido_paterno",
        preasignacion_de_carrera: "preasignacion_de_carrera",
        foto: "user.foto",
        curp: "curp",
        genero: "genero.genero",
        tipo_sangre: "tipo_sangre",
        telefonos: "telefonos",
        email: "user.email",
        grupo_actual: "grupo_actual",
      },
    },
    {
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  );

  //constantes para manejar la paginacion
  const est = estudiantes_data?.results
    ? estudiantes_data.results
    : estudiantes_data;
  const countTotal = estudiantes_data?.count;
  const previous = estudiantes_data?.previous;
  const next = estudiantes_data?.next;

  const [
    deleteEstudiante,
    {
      isSuccess: isSuccessD,
      isLoading: isLoadingD,
      isError: isErrorD,
      error: errorD,
    },
  ] = useDeleteEstudianteMutation();

  const dataTableEstudiante = {
    columns: [
      {
        Header: "Matrícula",
        accessor: "numero_de_control",
        Cell: ({ value }) => {
          return value ? value : "-------------";
        },
      },
      {
        Header: "Apellido Paterno",
        accessor: "apellido_paterno",
        Cell: ({ value }) => {
          return value ? value : "-------------";
        },
      },
      {
        Header: "Apellido Materno",
        accessor: "apellido_materno",
        Cell: ({ value }) => {
          return value ? value : "-------------";
        },
      },
      {
        Header: "Nombre(s)",
        accessor: "nombre",
        width: "20%",
        Cell: ({ value }) => {
          return value ? value : "-------------";
        },
      },
      {
        Header: "Acciones",
        accessor: "actions",
        width: "1%",
        Cell: ({ row }) => {
          const { original } = row;

          return (
            <MDBox display="flex">
              <ComponentsPrivatization
                permitted={[AUXILIAR_DOCENTE, COORDINADOR_DOCENTE]}
              >
                <MDBox px={0.2}>
                  <Tooltip
                    placement="top"
                    title="Agregar Antecedente Académico"
                  >
                    <Icon
                      style={style_icon}
                      onClick={() => handleClickCreateAntecedente(original)}
                      fontSize="small"
                    >
                      school
                    </Icon>
                  </Tooltip>
                </MDBox>
                <MDBox px={0.2}>
                  <Tooltip
                    placement="top"
                    title="Editar la información del estudiante"
                  >
                    <Icon
                      style={style_icon}
                      onClick={() => handleClickEdit(original)}
                      fontSize="small"
                    >
                      edit
                    </Icon>
                  </Tooltip>
                </MDBox>
              </ComponentsPrivatization>
              <ComponentsPrivatization permitted={[COORDINADOR_DOCENTE]}>
                <MDBox px={0.2}>
                  <Tooltip
                    placement="top"
                    title="Eliminar el estudiante del listado"
                  >
                    <Icon
                      style={style_icon}
                      onClick={() =>
                        handleToast({
                          title: "¿Estás seguro?",
                          text: `¡Si eliminas a: "${original.nombre}", la acción no se podrá revertir!`,
                          dispatcher: (result) => {
                            if (result.isConfirmed) {
                              deleteEstudiante(original.id);
                            }
                          },
                        })
                      }
                      fontSize="small"
                    >
                      remove_circle
                    </Icon>
                  </Tooltip>
                </MDBox>
              </ComponentsPrivatization>
            </MDBox>
          );
        },
      },
    ],

    rows: est ?? [],
  };

  useRedirectForm(
    isLoadingG,
    null,
    isErrorG,
    errorG,
    "Hubo problemas para cargar la información",
    ESTUDIANTES_LIST_URL,
  );

  useRedirectForm(
    isLoadingD,
    isSuccessD,
    isErrorD,
    errorD,
    "Estudiante eliminado",
    ESTUDIANTES_LIST_URL,
  );

  const handleClickEdit = ({ id }) => {
    setStudentIdToEdit(id);
    handleOpenStudentModal();
  };

  const handleClickCreateAntecedente = (student) => {
    setSelectedStudentForAntecedente(student);
    handleOpenAntecedenteModal();
  };

  const actions = (
    <MDBox display="flex" gap={2} alignItems="center">
      <ComponentsPrivatization
        permitted={[COORDINADOR_DOCENTE, AUXILIAR_DOCENTE]}
      >
        <MDBox display={"flex"} gap={1} alignItems={"center"}>
          <Tooltip
            placement="top"
            title="Crear nuevo estudiante con toda su información requerida"
          >
            <MDButton
              variant="contained"
              color="accent"
              size="medium"
              onClick={() => {
                setStudentIdToEdit(null);
                handleOpenStudentModal();
              }}
            >
              nuevo
            </MDButton>
          </Tooltip>

          <Tooltip title={"Descargar plantilla"} placement={"top"}>
            <a
              href={excelFile}
              download="Descargar plantilla excel"
              target="_blank"
              rel="noreferrer"
            >
              <MDButton
                variant="contained"
                color="accent"
                size="medium"
                iconOnly
              >
                <Icon fontSize={"large"}>cloud_download</Icon>
              </MDButton>
            </a>
          </Tooltip>
          <Tooltip title={"Importar fichero excel"} placement={"top"}>
            <MDButton
              variant="contained"
              color="accent"
              size="medium"
              iconOnly
              onClick={handleOpen}
            >
              <Icon fontSize={"large"}>cloud_upload</Icon>
            </MDButton>
          </Tooltip>
        </MDBox>
      </ComponentsPrivatization>
    </MDBox>
  );

  return (
    <MDBox pt={0} pb={3}>
      <ExcelModal open={open} handleClose={handleClose} />
      {openStudentModal && (
        <NewStudent
          open={openStudentModal}
          handleClose={() => {
            handleCloseStudentModal();
            setStudentIdToEdit(null);
          }}
          id={studentIdToEdit}
        />
      )}
      {openAntecedenteModal && (
        <NewAntecedente
          open={openAntecedenteModal}
          handleClose={() => {
            handleCloseAntecedenteModal();
            setSelectedStudentForAntecedente(null);
          }}
          estudianteUrl={selectedStudentForAntecedente?.url}
          nombreEstudiante={`${selectedStudentForAntecedente?.nombre} ${selectedStudentForAntecedente?.apellido_paterno} ${selectedStudentForAntecedente?.apellido_materno}`}
          curpEstudiante={selectedStudentForAntecedente?.curp}
        />
      )}
      <MDBox display="flex" justifyContent={"center"} alignItems={"center"}>
        {isLoadingG || isFetching ? (
          <CircularProgress sx={{ marginBottom: "3rem" }} color="inherit" />
        ) : (
          <DataTable
            tableHeader={tableHeader}
            actions={actions}
            table={dataTableEstudiante}
            canSearch
            paginationApi
            countTotal={countTotal}
            next={next}
            previous={previous}
            handleNext={handleNext}
            handlePrevious={handlePrevious}
            setBusqueda={setBusqueda}
            offset={offset}
            setOffset={setOffset}
            filtersComponent={
              <FiltersStudentComponent
                grupos={gruposOrdenados}
                carreras={carrerasOrdenadas}
                values={values}
                handleChange={handleChange}
              />
            }
            collapse
            showDetailsOnDoubleClick
            detailsComponent={(rowData) => (
              <MDBox>
                <EstudianteShow rowData={rowData} />
                <AntecedentesShow rowData={rowData} />
              </MDBox>
            )}
          />
        )}
      </MDBox>
    </MDBox>
  );
}

DataTableEstudiantes.propTypes = {
  tableHeader: PropTypes.string.isRequired,
};

export default DataTableEstudiantes;
